import { useCallback, useMemo } from 'react';
import { resolveRequestBodyRef } from '../../../../utils/apiRenderingUtils';
import { formValuesToJson } from '../../../../utils/requestBodyUtils';

/**
 * Custom hook for generating API request commands in different formats
 * Supports cURL and Fetch formats
 */
const useRequestGenerator = ({
  method,
  endpoint,
  parameters = [],
  requestBody = {},
  fullSpec,
  serverUrl = '',
  formValues = {},
}) => {
  // Get the request body schema from the OpenAPI spec
  const bodySchema = useMemo(
    () => Object.values(requestBody?.content ?? {})?.find((value) => value?.schema)?.schema,
    [requestBody]
  );

  const resolvedBodySchema = useMemo(() => resolveRequestBodyRef(bodySchema, fullSpec), [bodySchema, fullSpec]);

  // Generate cURL command
  const generateCurlCommand = useCallback(
    (values = {}) => {
      const headers = parameters
        ?.filter((param) => param?.in === 'header')
        .map((param) => `--header '${param?.name}: ${values[param?.name] || param?.example || ''}'`)
        .join(' \\\n');

      const queryParams = parameters
        .filter((param) => param?.in === 'query')
        .reduce((params, param) => {
          const value = values[param?.name] || param?.example || '';
          if (value) {
            params.append(param?.name, value);
          }
          return params;
        }, new URLSearchParams());

      const pathParams = parameters
        .filter((param) => param?.in === 'path')
        .reduce((url, param) => {
          return url.replace(`{${param?.name}}`, values[param?.name] || '');
        }, endpoint);

      const baseUrl = `${serverUrl}${pathParams}`;
      const queryString = queryParams.toString();
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      let command = `curl --request ${method} \\\n--url '${url}'`;

      if (headers) {
        command += ` \\\n${headers}`;
      }

      // Add request body data if present
      if (resolvedBodySchema) {
        const bodyData = formValuesToJson(values, resolvedBodySchema, fullSpec);
        if (Object.keys(bodyData).length > 0) {
          command += ` \\\n--header 'Content-Type: application/json' \\\n--data '${JSON.stringify(bodyData)}'`;
        }
      }

      return command;
    },
    [parameters, endpoint, method, resolvedBodySchema, serverUrl, fullSpec]
  );

  // Generate Fetch command
  const generateFetchCommand = useCallback(
    (values = {}) => {
      const headers = parameters
        ?.filter((param) => param?.in === 'header')
        ?.reduce((acc, param) => {
          acc[param?.name] = values[param?.name] || param?.example || '';
          return acc;
        }, {});

      if (resolvedBodySchema) {
        headers['Content-Type'] = 'application/json';
      }

      const queryParams = parameters
        ?.filter((param) => param?.in === 'query')
        ?.reduce((params, param) => {
          const value = values[param?.name] || param?.example || '';
          if (value) {
            params.append(param?.name, value);
          }
          return params;
        }, new URLSearchParams());

      const pathParams = parameters
        ?.filter((param) => param?.in === 'path')
        ?.reduce((url, param) => {
          return url?.replace(`{${param?.name}}`, values[param?.name] || '');
        }, endpoint);

      const baseUrl = `${serverUrl}${pathParams}`;
      const queryString = queryParams.toString();
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const fetchOptions = {
        method: method,
        headers: headers,
      };

      if (resolvedBodySchema) {
        const bodyData = formValuesToJson(values, resolvedBodySchema, fullSpec);
        if (Object?.keys(bodyData)?.length > 0) {
          fetchOptions.body = JSON.stringify(bodyData);
        }
      }

      return `fetch('${url}', ${JSON.stringify(fetchOptions, null, 2)})`;
    },
    [parameters, endpoint, method, resolvedBodySchema, serverUrl, fullSpec]
  );

  // Generate request based on format
  const generateRequest = useCallback(
    (format = 'curl', values = {}) => {
      return format === 'curl' ? generateCurlCommand(values) : generateFetchCommand(values);
    },
    [generateCurlCommand, generateFetchCommand]
  );

  return {
    generateRequest,
    generateCurlCommand,
    generateFetchCommand,
    resolvedBodySchema,
  };
};

export default useRequestGenerator;
